/**
 * SW2-2474 — getting a user back into their downloaded projects when the app is
 * reopened with no connection, and checking them again once it returns.
 *
 * At start the app asks the server whether the session is valid. Offline that
 * request cannot be answered (the service worker never serves it from cache),
 * and it used to read as "logged out", sending the user to a login page that
 * cannot work offline. Now a failure to reach the server restores the session
 * saved at the last successful check, for up to OFFLINE_SESSION_MAX_AGE_DAYS.
 * Only an answer from the server logs the user out.
 *
 * A restored session is unverified: `OfflineSessionGate` checks it again as
 * soon as the server can be reached, and nothing uploads until it has.
 */
import db, { removeOfflineProjects } from '../db/db';
import xhrService from './xhrService';

/**
 * How long after the server last confirmed the session it may be restored
 * offline. Counted from the last confirmation, not the login, so any time
 * online resets it.
 */
export const OFFLINE_SESSION_MAX_AGE_DAYS = 7;

const MAX_AGE_MS = OFFLINE_SESSION_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

/**
 * Set synchronously on logout. Logout reloads the page before `logout()` runs
 * (Logout.tsx), so the IndexedDB clear may never finish; this cannot be lost,
 * and a restore refuses while it is set.
 */
const SIGNED_OUT_KEY = 'offlineSessionSignedOut';

/**
 * Deactivations the device has not finished deleting. Each is added before its
 * delete starts and removed when that delete completes, so one that fails or is
 * cut off is run again at the next start, before anything can upload. A list:
 * on a shared device more than one user's delete can be outstanding.
 */
const PENDING_WIPE_KEY = 'offlineWipePending';

/**
 * Who the server says is deactivated. `scope` is "account" when the user is
 * deactivated everywhere, "tenant" when only in `tenantId`.
 */
export type Deactivation = { scope: 'account' | 'tenant'; userId: number; tenantId: number };

/**
 * `otherTenant`: the cookie is valid, but for another tenant, and switching to
 * this one needs verification (`isLoggedIn: false`). The user is not logged out
 * — sending them to sign in here must not end their other tenant's session.
 */
export type SessionCheck =
  | { state: 'loggedIn' }
  | { state: 'loggedOut'; deactivation: Deactivation | null; otherTenant?: boolean }
  | { state: 'unreachable' };

const endsSession = (status?: number) => status === 401 || status === 403;

const sameWipe = (a: Deactivation, b: Deactivation) =>
  a.userId === b.userId && a.scope === b.scope && a.tenantId === b.tenantId;

/** Throws if storage is blocked; callers decide what that means. */
const readPendingWipes = (): Deactivation[] => {
  const value = JSON.parse(localStorage.getItem(PENDING_WIPE_KEY) || '[]');

  return Array.isArray(value) ? value : [];
};

const writePendingWipes = (list: Deactivation[]) => {
  if (list.length) localStorage.setItem(PENDING_WIPE_KEY, JSON.stringify(list));
  else localStorage.removeItem(PENDING_WIPE_KEY);
};

const readDeactivation = (data: any): Deactivation | null =>
  data?.reason === 'deactivated' && data.userId != null && data.tenantId != null
    ? {
        scope: data.scope === 'account' ? 'account' : 'tenant',
        userId: Number(data.userId),
        tenantId: Number(data.tenantId),
      }
    : null;

/**
 * Ask the server whether the session is valid.
 *
 * Only a 401/403, or an explicit `isLoggedIn: false`, is "logged out" — and a
 * deactivation only when the server says so on a 401, because that answer
 * deletes the user's downloads. Anything else says nothing about the session
 * and is "unreachable": no response, a 5xx while the API is down or deploying,
 * a rate limit, a captive portal's page. The session is then kept as it is.
 */
export async function checkSession(params: Record<string, any> = {}): Promise<SessionCheck> {
  try {
    const { data } = await xhrService.get('/api/users/isLoggedIn', { params });

    if (data?.isLoggedIn === true) return { state: 'loggedIn' };
    if (data?.isLoggedIn === false) return { state: 'loggedOut', deactivation: null };

    return { state: 'unreachable' };
  } catch (err) {
    const status = err?.response?.status;

    if (!endsSession(status)) return { state: 'unreachable' };

    if (err.response.data?.isLoggedIn === false) {
      return { state: 'loggedOut', deactivation: null, otherTenant: true };
    }

    return {
      state: 'loggedOut',
      deactivation: status === 401 ? readDeactivation(err.response.data) : null,
    };
  }
}

/** Whether a failed request said nothing about the session (see `checkSession`). */
export const isUnreachable = (err: any) => !endsSession(err?.response?.status);

/** The deactivation a failed request reports, if any (see `checkSession`). */
export const deactivationOf = (err: any): Deactivation | null =>
  err?.response?.status === 401 ? readDeactivation(err.response.data) : null;

/**
 * Store the user the server just returned. Never throws.
 *
 * `restorable` is false on public pages: their session is a visitor's non-user
 * token that resolves to a real user record, and must never be restorable as
 * that user's login. The row is still written, as before, without the profile.
 */
export async function saveSession(appData: any, { restorable }: { restorable: boolean }) {
  const { appConstants, ...user } = appData;

  if (!restorable) {
    try {
      // A signed-in user opening a public page keeps their session and their
      // claim on the work on this device; the visitor token must not replace them.
      if ((await db.user.get(1))?.profile) return;
    } catch {
      // Unreadable: fall through to the write below, which reports its own failure.
    }
  }

  if (restorable) {
    try {
      localStorage.removeItem(SIGNED_OUT_KEY);

      // The server has just confirmed this user: an unfinished delete from
      // their deactivation is obsolete (they were reactivated), and must not
      // later delete the work they do from now on. A tenant's delete only by a
      // confirmation in that tenant — being active in another one says nothing.
      const confirmed = (p: Deactivation) =>
        p.userId === Number(user.id) &&
        (p.scope === 'account' || p.tenantId === Number(user.tenantId));

      writePendingWipes(readPendingWipes().filter((p) => !confirmed(p)));
    } catch {
      // Storage blocked: the restore then refuses, which is the safe side.
    }
  }

  try {
    await db.user.put({
      id: 1,
      user_id: user.id,
      tenant_id: user.tenantId,
      first_name: user.first_name,
      last_name: user.last_name,
      name: user.name,
      email: user.email,
      role: user.roleName,
      ...(restorable && { profile: appData, verified_at: new Date().toISOString() }),
    });
  } catch (dbError) {
    // eslint-disable-next-line no-console
    console.warn('IndexedDB write failed (non-fatal):', dbError);
  }
}

/** The saved profile, if it may be used for an offline restore; otherwise null. */
export async function loadSavedSession(): Promise<any | null> {
  try {
    if (localStorage.getItem(SIGNED_OUT_KEY)) return null;

    const row = await db.user.get(1);
    const verifiedAt = Date.parse(row?.verified_at || '');
    const age = Date.now() - verifiedAt;

    // A negative age means the clock was moved back past the last check.
    if (!row?.profile || Number.isNaN(verifiedAt) || age < 0 || age > MAX_AGE_MS) {
      return null;
    }

    return row.profile;
  } catch {
    return null;
  }
}

/**
 * Whether this device was signed in — a saved session not ended by a logout,
 * however old. Tells a session that ended under the user (show why) from a
 * visitor who was never signed in (just show the login page).
 */
export async function hasSavedSession(): Promise<boolean> {
  try {
    if (localStorage.getItem(SIGNED_OUT_KEY)) return false;

    return Boolean((await db.user.get(1))?.profile);
  } catch {
    return false;
  }
}

/**
 * On logout: no offline restore until the next successful login. The user id
 * and tenant stay on the row — the sync uses them to tell whose work is whose.
 */
export function forgetSavedSession() {
  try {
    localStorage.setItem(SIGNED_OUT_KEY, '1');
  } catch {
    // Nothing else to do; the IndexedDB clear below may still land.
  }

  const signedOutAt = Date.now();

  // Deferred into a promise: Dexie throws synchronously when IndexedDB is
  // unavailable (blocked storage, private modes), and logout must not. Clears
  // only a session confirmed before this logout — if it lands late, the next
  // login's session must survive it.
  Promise.resolve()
    .then(() =>
      db.transaction('rw', db.user, async () => {
        const row = await db.user.get(1);

        if (row?.verified_at && Date.parse(row.verified_at) >= signedOutAt) return;

        await db.user.update(1, { profile: undefined, verified_at: undefined });
      }),
    )
    // eslint-disable-next-line no-console
    .catch((err) => console.warn('Clearing the saved session failed (non-fatal):', err));
}

/**
 * The server says a user is deactivated: delete what they downloaded, their
 * queued work included, and the saved session. Nothing is uploaded first —
 * work from a deactivated user is never trusted onto the server (SW2-2474
 * option 1).
 *
 * Only for this device's user. The rejected token can be a public-link visitor
 * token carrying another user's id; that says nothing about the user signed in
 * here, and returns false — the caller treats it as an ordinary logout. So does
 * a device with no saved user: there is nothing of theirs to delete.
 *
 * Downloads are matched on `downloadedBy`, and on `downloadedTenant` when only
 * one tenant is deactivated. A download made before `downloadedTenant` existed
 * has no tenant: it is deleted only when the whole account is deactivated.
 * Legacy downloads with no owner at all are left alone.
 *
 * Returns true when the data was this user's. A delete that fails is kept
 * pending and run again at the next start (`resumePendingWipe`).
 */
export async function wipeDeactivatedUser(deactivation: Deactivation): Promise<boolean> {
  try {
    const row = await db.user.get(1);

    if (row?.user_id == null || Number(row.user_id) !== deactivation.userId) return false;
  } catch {
    // Unreadable: the delete below would fail the same way, and stays pending.
  }

  try {
    writePendingWipes([...readPendingWipes().filter((p) => !sameWipe(p, deactivation)), deactivation]);
    localStorage.setItem(SIGNED_OUT_KEY, '1');
  } catch {
    // Storage blocked: the delete still runs now; it just cannot be retried.
  }

  await runWipe(deactivation);

  return true;
}

/** At start-up: finish a deactivation's delete that did not complete. Never throws. */
export async function resumePendingWipe() {
  let pending: Deactivation[] = [];

  try {
    pending = readPendingWipes();
  } catch {
    return;
  }

  for (const deactivation of pending) {
    await runWipe(deactivation);
  }
}

async function runWipe(deactivation: Deactivation) {
  const { scope, userId, tenantId } = deactivation;
  const ownedHere = (p: any) =>
    Number(p.downloadedBy) === userId &&
    (scope === 'account' || Number(p.downloadedTenant) === tenantId);

  try {
    await db.transaction('rw', db.tables, async () => {
      const projects = await db.projects.filter(ownedHere).toArray();

      for (const project of projects) {
        await removeOfflineProjects(project.id);
      }

      await db.tenantFeatures
        .filter(
          (r: any) =>
            typeof r.key === 'string' &&
            (scope === 'account'
              ? r.key.endsWith(`:offline-downloads:${userId}`)
              : r.key === `${tenantId}:offline-downloads:${userId}`),
        )
        .delete();

      // A retry may run after someone else has signed in on this device.
      if (Number((await db.user.get(1))?.user_id) === userId) await db.user.delete(1);
    });

  } catch (err) {
    // Stays pending; resumePendingWipe runs it again at the next start.
    // eslint-disable-next-line no-console
    console.error('Deleting a deactivated user\'s offline data failed', err);

    return;
  }

  try {
    writePendingWipes(readPendingWipes().filter((p) => !sameWipe(p, deactivation)));
  } catch {
    // Storage blocked: nothing was recorded as pending either.
  }
}
